import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, LogOut, Save, Camera, Edit3, User, Loader2, CheckCircle, Clock, XCircle, ChevronDown, ChevronUp, Users, Plus, Trash2, AlertTriangle, Upload, MessageSquare, Paperclip, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { resolveFileUrl } from '../../utils/fileUrl';
import { toast } from 'sonner';
import { formatDisplayDate } from '../../lib/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const MARITAL_STATUS_OPTIONS = ['Unmarried / अविवाहित', 'Divorced / तलाकशुदा', 'Widow / विधवा', 'Widower / विधुर'];
const COMPLEXION_OPTIONS = ['Very Fair / बहुत गोरा', 'Fair / गोरा', 'Wheatish / गेहुआँ', 'Dark / साँवला'];
const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

const PSDashboardPage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [originalCounts, setOriginalCounts] = useState({ candidates: 0, attendees: 0, attendingInPerson: false });
  const [nukhOptions, setNukhOptions] = useState([]);
  const [expandedCandidate, setExpandedCandidate] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [uploadingAttendeePhoto, setUploadingAttendeePhoto] = useState(null);
  const [additionalPayment, setAdditionalPayment] = useState({ mode: '', details: '', proofUrl: '' });
  const [uploadingPaymentProof, setUploadingPaymentProof] = useState(false);
  const [uploadingInfoAttachment, setUploadingInfoAttachment] = useState(false);
  const paymentProofRef = useRef(null);
  const photoRefs = useRef({});
  const attendeePhotoRefs = useRef({});

  const token = localStorage.getItem('psToken');

  useEffect(() => {
    if (!token) { navigate('/parichay-sangam/login'); return; }
    fetchProfile();
    fetchOptions();
  }, [token, navigate]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/ps/profile`, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data);
      setEditData(JSON.parse(JSON.stringify(res.data)));
    } catch (err) {
      if (err.response?.status === 401) handleLogout();
      else toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try { const res = await axios.get(`${API}/ps/options`); setNukhOptions(res.data.nukhs || []); } catch {}
  };

  const handleLogout = () => {
    localStorage.removeItem('psToken');
    localStorage.removeItem('psUserId');
    localStorage.removeItem('psUserName');
    navigate('/parichay-sangam/login');
    toast.success('Logged out');
  };

  // Calculate additional heads needed when editing
  const getAdditionalHeads = () => {
    if (!editMode || !profile) return 0;
    const wasAttending = originalCounts.attendingInPerson;
    const nowAttending = !!editData.attendingInPerson;

    const newCandidates = editData.candidates?.length || 0;
    const newAttendees = (editData.attendees || []).length;
    const newTotal = newCandidates + newAttendees;

    if (!nowAttending) return 0; // Not attending → no payment needed

    if (!wasAttending) {
      // Switched from not-attending to attending → pay for ALL heads
      return newTotal;
    }

    // Was already attending → pay only for additional heads
    const oldTotal = originalCounts.candidates + originalCounts.attendees;
    return Math.max(0, newTotal - oldTotal);
  };

  const additionalHeads = editMode ? getAdditionalHeads() : 0;
  const additionalAmount = additionalHeads * 1000;
  const isApprovedEdit = editMode && profile?.status === 'approved';

  const handlePaymentProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`File size is ${(file.size / (1024 * 1024)).toFixed(1)}MB. Please upload less than 5MB.`);
      e.target.value = ''; return;
    }
    setUploadingPaymentProof(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/ps/profile/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setAdditionalPayment(prev => ({ ...prev, proofUrl: res.data.url }));
      toast.success('Payment proof uploaded!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Upload failed. Please try again.');
    }
    finally { setUploadingPaymentProof(false); }
  };

  const handleSave = async () => {
    // Validate all candidates
    const requiredFields = [
      { key: 'name', label: 'Full Name' }, { key: 'fatherName', label: "Father's Name" },
      { key: 'gender', label: 'Gender' }, { key: 'dateOfBirth', label: 'Date of Birth' },
      { key: 'height', label: 'Height' }, { key: 'weight', label: 'Weight' },
      { key: 'complexion', label: 'Complexion' }, { key: 'education', label: 'Education' },
      { key: 'occupation', label: 'Occupation' }, { key: 'workingAt', label: 'Working At' },
      { key: 'phone', label: 'Phone' }, { key: 'whatsapp', label: 'WhatsApp' },
      { key: 'email', label: 'Email' }, { key: 'currentAddress', label: 'Address' },
      { key: 'currentPlace', label: 'Place' }, { key: 'currentState', label: 'State' },
      { key: 'currentCountry', label: 'Country' }, { key: 'nativePlace', label: 'Native Place' },
      { key: 'nativeState', label: 'Native State' },
      { key: 'unmarriedBrothers', label: 'Unmarried Brothers' }, { key: 'marriedBrothers', label: 'Married Brothers' },
      { key: 'unmarriedSisters', label: 'Unmarried Sisters' }, { key: 'marriedSisters', label: 'Married Sisters' },
      { key: 'maternalName', label: 'Maternal Name' }, { key: 'nukhOfMaternals', label: 'Nukh of Maternals' },
      { key: 'placeOfMaternals', label: 'Place of Maternals' },
    ];

    for (let i = 0; i < (editData.candidates || []).length; i++) {
      const c = editData.candidates[i];
      for (const { key, label } of requiredFields) {
        if (!String(c[key] || '').trim()) {
          toast.error(`"${label}" is required for Candidate ${i + 1}`);
          setExpandedCandidate(i);
          return;
        }
      }
      if (!/^\d{10}$/.test(c.phone?.trim())) { toast.error(`Phone must be 10 digits for Candidate ${i + 1}`); setExpandedCandidate(i); return; }
      if (!/^\d{10}$/.test(c.whatsapp?.trim())) { toast.error(`WhatsApp must be 10 digits for Candidate ${i + 1}`); setExpandedCandidate(i); return; }
    }

    // Validate attendees (all named attendees must have relation, phone, and photo)
    const namedAttendees = (editData.attendees || []).filter(a => a.name?.trim());
    for (const a of namedAttendees) {
      if (!a.relation?.trim()) { toast.error(`Relation is required for attendee "${a.name}"`); return; }
      if (!a.phone?.trim()) { toast.error(`Phone is required for attendee "${a.name}"`); return; }
      if (!a.photoUrl) { toast.error(`Photo is mandatory for attendee "${a.name}". It will be used for ID card.`); return; }
    }

    // Validate additional payment if new heads added
    if (additionalHeads > 0) {
      if (!additionalPayment.mode) { toast.error('Please select payment mode for additional amount'); return; }
      if (!additionalPayment.details?.trim()) { toast.error('Please enter transaction details for additional payment'); return; }
      if (!additionalPayment.proofUrl) { toast.error('Please upload payment proof for additional amount'); return; }
    }

    setSaving(true);
    try {
      const { password, _id, ...safeData } = editData;
      if (additionalHeads > 0) {
        safeData.additionalPaymentMode = additionalPayment.mode;
        safeData.additionalPaymentDetails = additionalPayment.details;
        safeData.additionalPaymentProofUrl = additionalPayment.proofUrl;
      }
      const res = await axios.put(`${API}/ps/profile`, safeData, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data.user);
      setEditData(JSON.parse(JSON.stringify(res.data.user)));
      setEditMode(false);
      setAdditionalPayment({ mode: '', details: '', proofUrl: '' });
      if (isApprovedEdit) {
        toast.success('Profile updated! Your registration is now pending re-approval.');
      } else {
        toast.success('Profile updated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (candidateIdx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`File size is ${(file.size / (1024 * 1024)).toFixed(1)}MB. Please upload a photo less than 5MB.`);
      e.target.value = ''; return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG or WEBP image.');
      e.target.value = ''; return;
    }
    setUploadingPhoto(candidateIdx);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('candidateIndex', candidateIdx.toString());
      const res = await axios.post(`${API}/ps/profile/photo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => {
        const candidates = [...(prev.candidates || [])];
        candidates[candidateIdx] = { ...candidates[candidateIdx], photoUrl: res.data.photoUrl };
        return { ...prev, candidates };
      });
      setEditData(prev => {
        const candidates = [...(prev.candidates || [])];
        candidates[candidateIdx] = { ...candidates[candidateIdx], photoUrl: res.data.photoUrl };
        return { ...prev, candidates };
      });
      toast.success('Photo updated!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Photo upload failed. Please try again.');
    }
    finally { setUploadingPhoto(null); }
  };

  const handleAttendeePhotoUpload = async (aidx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`File size is ${(file.size / (1024 * 1024)).toFixed(1)}MB. Please upload a photo less than 5MB.`);
      e.target.value = ''; return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG or WEBP image.');
      e.target.value = ''; return;
    }
    setUploadingAttendeePhoto(aidx);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('attendeeIndex', aidx.toString());
      const res = await axios.post(`${API}/ps/profile/attendee-photo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const newUrl = res.data.photoUrl;
      setProfile(prev => {
        const attendees = [...(prev.attendees || [])];
        attendees[aidx] = { ...attendees[aidx], photoUrl: newUrl };
        return { ...prev, attendees };
      });
      setEditData(prev => {
        const attendees = [...(prev.attendees || [])];
        attendees[aidx] = { ...attendees[aidx], photoUrl: newUrl };
        return { ...prev, attendees };
      });
      toast.success('Attendee photo updated!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Photo upload failed. Please try again.');
    }
    finally { setUploadingAttendeePhoto(null); }
  };

  const handleCandidateEdit = (cidx, field, value) => {
    setEditData(prev => {
      const candidates = [...(prev.candidates || [])];
      candidates[cidx] = { ...candidates[cidx], [field]: value };
      return { ...prev, candidates };
    });
  };

  const handleFamilyEdit = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleAttendeeEdit = (aidx, field, value) => {
    setEditData(prev => {
      const attendees = [...(prev.attendees || [])];
      attendees[aidx] = { ...attendees[aidx], [field]: value };
      return { ...prev, attendees };
    });
  };

  const addCandidate = () => {
    setEditData(prev => {
      const candidates = [...(prev.candidates || [])];
      if (candidates.length >= 3) return prev;
      candidates.push({
        name: '', fatherName: '', motherName: '', nukh: '', gender: '', maritalStatus: '',
        dateOfBirth: '', birthTime: '', birthPlace: '', height: '', weight: '', complexion: '',
        bloodGroup: '', mapiOrManglik: '', unmarriedBrothers: '', marriedBrothers: '',
        unmarriedSisters: '', marriedSisters: '', maternalName: '', nukhOfMaternals: '',
        placeOfMaternals: '', education: '', occupation: '', workingAt: '', annualIncome: '',
        phone: '', whatsapp: '', email: '', currentAddress: '', currentPlace: '', currentState: '',
        currentCountry: 'India', nativePlace: '', nativeState: '', hobbies: '', aboutCandidate: '',
        partnerPreference: '', photoUrl: '', candidateCode: '',
      });
      return { ...prev, candidates };
    });
    // Auto-expand the new candidate
    setExpandedCandidate(editData.candidates?.length || 0);
  };

  const removeCandidate = (cidx) => {
    const existing = profile.candidates || [];
    if (cidx < existing.length) { toast.error('Cannot remove original candidates'); return; }
    setEditData(prev => {
      const candidates = [...(prev.candidates || [])];
      candidates.splice(cidx, 1);
      return { ...prev, candidates };
    });
    if (expandedCandidate >= cidx) setExpandedCandidate(Math.max(0, expandedCandidate - 1));
  };

  const addAttendee = () => {
    setEditData(prev => {
      const attendees = [...(prev.attendees || [])];
      attendees.push({ name: '', relation: '', phone: '', photoUrl: '' });
      return { ...prev, attendees };
    });
  };

  const removeAttendee = (aidx) => {
    setEditData(prev => {
      const attendees = [...(prev.attendees || [])];
      attendees.splice(aidx, 1);
      return { ...prev, attendees };
    });
  };

  const StatusBadge = ({ status }) => {
    const styles = { approved: 'bg-green-100 text-green-700', pending: 'bg-yellow-100 text-yellow-700', rejected: 'bg-red-100 text-red-700' };
    const icons = { approved: CheckCircle, pending: Clock, rejected: XCircle };
    const Icon = icons[status] || Clock;
    return (<span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${styles[status] || styles.pending}`}><Icon className="w-3.5 h-3.5" />{status?.charAt(0).toUpperCase() + status?.slice(1)}</span>);
  };

  // Render a field for a specific candidate
  const renderCandidateField = (cidx, label, field, opts = {}) => {
    const candidate = editMode ? editData.candidates?.[cidx] : profile.candidates?.[cidx];
    const value = candidate?.[field] || '';
    const reqMark = opts.required ? <span className="text-destructive"> *</span> : null;
    if (!editMode || opts.readOnly) {
      const displayValue = field === 'dateOfBirth' ? (formatDisplayDate(value) || '\u2014') : (value || '\u2014');
      return (<div><p className="text-xs text-gray-400 mb-0.5">{label}</p><p className="text-sm font-medium text-gray-800">{displayValue}</p></div>);
    }
    if (opts.options) {
      return (<div><Label className="text-sm">{label}{reqMark}</Label><select value={value} onChange={(e) => handleCandidateEdit(cidx, field, e.target.value)} className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"><option value="">Select</option>{opts.options.map(o => <option key={o} value={o}>{o}</option>)}</select></div>);
    }
    if (opts.nukh) {
      return (<div><Label className="text-sm">{label}{reqMark}</Label><SearchableSelect options={nukhOptions} value={value} onChange={(v) => handleCandidateEdit(cidx, field, v)} placeholder="Search nukh..." className="mt-1" /></div>);
    }
    return (<div><Label className="text-sm">{label}{reqMark}</Label><Input type={opts.type || 'text'} value={value} onChange={(e) => handleCandidateEdit(cidx, field, e.target.value)} className="mt-1" /></div>);
  };

  const renderCandidateTextarea = (cidx, label, field) => {
    const candidate = editMode ? editData.candidates?.[cidx] : profile.candidates?.[cidx];
    const value = candidate?.[field] || '';
    if (!editMode) {
      return (<div><p className="text-xs text-gray-400 mb-0.5">{label}</p><p className="text-sm text-gray-800 whitespace-pre-wrap">{value || '\u2014'}</p></div>);
    }
    return (<div><Label className="text-sm">{label}</Label><Textarea value={value} onChange={(e) => handleCandidateEdit(cidx, field, e.target.value)} rows={2} className="mt-1" /></div>);
  };

  const renderFamilyField = (label, field) => {
    const value = editMode ? (editData[field] || '') : (profile?.[field] || '');
    if (!editMode) {
      return (<div><p className="text-xs text-gray-400 mb-0.5">{label}</p><p className="text-sm font-medium text-gray-800">{value || '\u2014'}</p></div>);
    }
    return (<div><Label className="text-sm">{label}</Label><Input value={value} onChange={(e) => handleFamilyEdit(field, e.target.value)} className="mt-1" /></div>);
  };

  if (loading) return (<div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-orange-600" /></div>);
  if (!profile) return null;

  const primaryCandidate = profile.candidates?.[0];
  const primaryName = primaryCandidate?.name || 'User';

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50" data-testid="ps-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 py-6">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_eb6102e5-96eb-40a4-9fc1-e76d00d027fa/artifacts/osywbjv5_Parichay%20Sangam%202026%20logo.jpeg"
              alt="Parichay Sangam 2026"
              className="w-12 h-12 rounded-lg shadow object-cover"
            />
            <div>
              <h1 className="text-xl font-bold text-white">Parichay Sangam 2026</h1>
              <p className="text-white/70 text-sm">Welcome, {primaryName} ({profile.familyCode})</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={profile.status} />
            <Button variant="outline" size="sm" onClick={handleLogout} className="bg-white/10 text-white border-white/30 hover:bg-white/20" data-testid="ps-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Edit/Save Controls - Hidden: Registration editing is now closed */}
        {false && (
        <div className="flex justify-end mb-4 gap-2">
          {!editMode ? (
            <Button variant="outline" size="sm" onClick={() => {
              setOriginalCounts({ candidates: profile.candidates?.length || 0, attendees: (profile.attendees || []).length, attendingInPerson: !!profile.attendingInPerson });
              setEditMode(true);
            }} data-testid="ps-edit-btn">
              <Edit3 className="w-4 h-4 mr-1" /> Edit Profile
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => { setEditMode(false); setEditData(JSON.parse(JSON.stringify(profile))); }}>Cancel</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="ps-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
              </Button>
            </>
          )}
        </div>
        )}

        {/* Re-approval Warning Banner */}
        {isApprovedEdit && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-4" data-testid="ps-reapproval-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-800">Your registration is currently approved</p>
                <p className="text-sm text-amber-700 mt-1">Saving changes will set your status back to <strong>"Pending Approval"</strong>. Admin will need to re-approve your registration and you will receive updated ID cards via email after re-approval.</p>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Reason & Admin Comments */}
        {profile?.status === 'rejected' && profile?.rejectionReason && (
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4" data-testid="ps-user-rejection-reason">
            <div className="flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-800">Registration Rejected / रजिस्ट्रेशन अस्वीकार</p>
                <p className="text-sm text-red-700 mt-1 whitespace-pre-wrap">{profile.rejectionReason}</p>
              </div>
            </div>
          </div>
        )}

        {profile?.adminComments && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 mb-4" data-testid="ps-user-admin-comments">
            <div className="flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-800">Admin Feedback / प्रशासन की टिप्पणी</p>
                <p className="text-sm text-amber-700 mt-1 whitespace-pre-wrap">{profile.adminComments}</p>
                <p className="text-xs text-amber-500 mt-2">Please review and edit your profile to address the feedback above, then re-submit.</p>
              </div>
            </div>
          </div>
        )}

        {/* Additional Payment Section (when new heads added or switched to attending) */}
        {editMode && additionalHeads > 0 && (
          <Card className="p-5 mb-4 border-2 border-orange-300 bg-orange-50/50" data-testid="ps-additional-payment-section">
            <h3 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Additional Payment Required
            </h3>
            <p className="text-sm text-orange-700 mb-4">
              You are adding <strong>{additionalHeads} new member(s)</strong>. Additional fee: <strong>{'\u20B9'}{additionalAmount.toLocaleString()}</strong> ({'\u20B9'}1,000 x {additionalHeads})
            </p>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Payment Mode / भुगतान का तरीका <span className="text-destructive">*</span></Label>
                <div className="flex gap-3 mt-1">
                  {['UPI', 'Bank Transfer'].map(mode => (
                    <Button key={mode} type="button" variant={additionalPayment.mode === mode ? 'default' : 'outline'} size="sm"
                      onClick={() => setAdditionalPayment(prev => ({ ...prev, mode }))}
                      className={additionalPayment.mode === mode ? 'bg-orange-600 hover:bg-orange-700' : ''}
                      data-testid={`ps-addl-payment-mode-${mode.toLowerCase().replace(' ', '-')}`}>
                      {mode}
                    </Button>
                  ))}
                </div>
              </div>
              {additionalPayment.mode && (
                <>
                  <div className="bg-white p-3 rounded border text-sm">
                    {additionalPayment.mode === 'UPI' ? (
                      <div className="text-center">
                        <h4 className="font-semibold text-blue-800 mb-3">UPI से भुगतान करें / Pay via UPI</h4>
                        <img
                          src="https://customer-assets.emergentagent.com/job_337dba5c-8d5d-4e7d-adac-9993bcb0c3e3/artifacts/uxuej4sd_WhatsApp%20Image%202026-02-15%20at%2013.14.34.jpeg"
                          alt="UPI QR Code - Scan to Pay"
                          className="max-w-[200px] w-full h-auto rounded-lg shadow-md mb-3 mx-auto"
                        />
                        <p className="text-sm text-blue-700 font-medium">UPI ID: boim-661059350985@boi</p>
                        <p className="text-xs text-blue-600 mt-1">किसी भी UPI ऐप से स्कैन करें / Scan with any UPI App</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <h4 className="font-semibold text-green-800 mb-2">बैंक विवरण / Bank Details</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="font-medium text-green-700">खाता नाम / Account Name:</span>
                            <p className="text-gray-800">BRAHMAKSHATRIYA MARVADI KHATRI ASSOCIATION</p>
                          </div>
                          <div>
                            <span className="font-medium text-green-700">बैंक का नाम / Bank Name:</span>
                            <p className="text-gray-800">BANK OF INDIA</p>
                          </div>
                          <div>
                            <span className="font-medium text-green-700">खाता संख्या / Account No:</span>
                            <p className="text-gray-800 font-mono select-all">661020110000985</p>
                          </div>
                          <div>
                            <span className="font-medium text-green-700">IFSC कोड / IFSC Code:</span>
                            <p className="text-gray-800 font-mono select-all">BKID0006610</p>
                          </div>
                          <div>
                            <span className="font-medium text-green-700">शाखा / Branch:</span>
                            <p className="text-gray-800">JODHPUR, RAJASTHAN</p>
                          </div>
                          <div>
                            <span className="font-medium text-green-700">MICR कोड / MICR Code:</span>
                            <p className="text-gray-800 font-mono">342013002</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">Transaction ID / UTR Number <span className="text-destructive">*</span></Label>
                    <Input value={additionalPayment.details} onChange={(e) => setAdditionalPayment(prev => ({ ...prev, details: e.target.value }))} placeholder="Enter transaction reference" className="mt-1" data-testid="ps-addl-payment-details" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Payment Proof Screenshot <span className="text-destructive">*</span></Label>
                    <div className="mt-1">
                      {additionalPayment.proofUrl ? (
                        <div className="flex items-center gap-3">
                          <img src={resolveFileUrl(additionalPayment.proofUrl)} alt="Proof" className="w-20 h-20 object-cover rounded border" />
                          <Button variant="outline" size="sm" onClick={() => paymentProofRef.current?.click()}>Change</Button>
                        </div>
                      ) : (
                        <Button variant="outline" onClick={() => paymentProofRef.current?.click()} disabled={uploadingPaymentProof} className="border-dashed" data-testid="ps-addl-payment-proof-upload">
                          {uploadingPaymentProof ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                          Upload Payment Proof
                        </Button>
                      )}
                      <input ref={paymentProofRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePaymentProofUpload} />
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>
        )}

        {/* Family Info Card */}
        <Card className="p-6 mb-6">
          <h3 className="font-bold text-lg text-gray-900 mb-4 border-b pb-2">Family Details / परिवार विवरण</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Family Code</p>
              <p className="text-sm font-bold text-orange-700">{profile.familyCode}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Email</p>
              <p className="text-sm font-medium text-gray-800">{profile.email}</p>
            </div>
            {renderFamilyField("Guardian Name / अभिभावक", "guardianName")}
            {renderFamilyField("Guardian WhatsApp / अभिभावक व्हाट्सएप", "guardianWhatsapp")}
          </div>
        </Card>

        {/* Candidates */}
        {(editMode ? editData.candidates : profile.candidates)?.map((candidate, cidx) => {
          const displayCandidate = editMode ? editData.candidates?.[cidx] : candidate;
          const isExpanded = expandedCandidate === cidx;
          const isNewCandidate = cidx >= (profile.candidates?.length || 0);
          return (
            <Card key={cidx} className={`mb-6 overflow-hidden ${isNewCandidate ? 'border-2 border-dashed border-orange-300' : ''}`}>
              <div className="flex items-center justify-between p-4 bg-orange-50 cursor-pointer" onClick={() => setExpandedCandidate(isExpanded ? -1 : cidx)} data-testid={`ps-dash-candidate-${cidx}-header`}>
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-600" />
                  {isNewCandidate ? 'New ' : ''}Candidate {cidx + 1}: {displayCandidate?.name || '(Fill details)'}
                  {displayCandidate?.candidateCode && <span className="text-sm font-normal text-orange-600">({displayCandidate.candidateCode})</span>}
                  {isNewCandidate && <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full ml-2">New</span>}
                </h3>
                <div className="flex items-center gap-2">
                  {editMode && isNewCandidate && (
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeCandidate(cidx); }} className="text-red-500 hover:text-red-700 h-8 w-8 p-0" data-testid={`ps-remove-candidate-${cidx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </div>

              {isExpanded && (
                <div className="p-6 space-y-6">
                  {/* Photo + Quick Info */}
                  <div className="flex items-start gap-6">
                    <div className="relative group">
                      <div className="w-28 h-28 rounded-xl overflow-hidden bg-gray-100 border-2 border-orange-200">
                        {displayCandidate?.photoUrl ? (
                          <img src={resolveFileUrl(displayCandidate.photoUrl)} alt={displayCandidate.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><User className="w-10 h-10 text-gray-300" /></div>
                        )}
                      </div>
                      <button onClick={() => photoRefs.current[cidx]?.click()} disabled={uploadingPhoto === cidx}
                        className="absolute bottom-1 right-1 bg-orange-600 text-white p-1.5 rounded-full shadow-lg hover:bg-orange-700" data-testid={`ps-photo-change-${cidx}`}>
                        {uploadingPhoto === cidx ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                      </button>
                      <input ref={el => photoRefs.current[cidx] = el} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handlePhotoUpload(cidx, e)} />
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-500 text-sm flex flex-wrap gap-3">
                        <span>{displayCandidate?.gender}</span>
                        {displayCandidate?.dateOfBirth && <span>DOB: {formatDisplayDate(displayCandidate.dateOfBirth)}</span>}
                        {displayCandidate?.currentPlace && <span>{displayCandidate.currentPlace}, {displayCandidate.currentState}</span>}
                        {displayCandidate?.education && <span>{displayCandidate.education}</span>}
                      </p>
                    </div>
                  </div>

                  {/* Personal */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2 text-sm">Personal Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {renderCandidateField(cidx, "Full Name", "name", { required: true })}
                      {renderCandidateField(cidx, "Father's Name", "fatherName", { required: true })}
                      {renderCandidateField(cidx, "Mother's Name", "motherName")}
                      {renderCandidateField(cidx, "Nukh", "nukh", { nukh: true })}
                      {renderCandidateField(cidx, "Gender", "gender", { required: true, readOnly: true })}
                      {renderCandidateField(cidx, "Marital Status", "maritalStatus", { options: editMode ? MARITAL_STATUS_OPTIONS : undefined })}
                      {renderCandidateField(cidx, "Date of Birth", "dateOfBirth", { required: true, type: editMode ? 'date' : 'text' })}
                      {renderCandidateField(cidx, "Birth Time", "birthTime", { type: editMode ? 'time' : 'text' })}
                      {renderCandidateField(cidx, "Birth Place", "birthPlace")}
                    </div>
                  </div>

                  {/* Physical */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2 text-sm">Physical Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {renderCandidateField(cidx, "Height", "height", { required: true })}
                      {renderCandidateField(cidx, "Weight (kg)", "weight", { required: true, type: editMode ? 'number' : 'text' })}
                      {renderCandidateField(cidx, "Complexion", "complexion", { required: true, options: editMode ? COMPLEXION_OPTIONS : undefined })}
                      {renderCandidateField(cidx, "Blood Group", "bloodGroup", { options: editMode ? BLOOD_GROUP_OPTIONS : undefined })}
                      {renderCandidateField(cidx, "Manglik", "mapiOrManglik", { options: editMode ? ['Yes / हाँ', 'No / नहीं', 'Partial / आंशिक'] : undefined })}
                    </div>
                  </div>

                  {/* Family */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2 text-sm">Family Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      {renderCandidateField(cidx, "Unmarried Brothers", "unmarriedBrothers", { required: true, type: editMode ? 'number' : 'text' })}
                      {renderCandidateField(cidx, "Married Brothers", "marriedBrothers", { required: true, type: editMode ? 'number' : 'text' })}
                      {renderCandidateField(cidx, "Unmarried Sisters", "unmarriedSisters", { required: true, type: editMode ? 'number' : 'text' })}
                      {renderCandidateField(cidx, "Married Sisters", "marriedSisters", { required: true, type: editMode ? 'number' : 'text' })}
                    </div>
                    <h5 className="font-medium text-gray-600 mb-2 text-sm">Maternal Details</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {renderCandidateField(cidx, "Maternal Name", "maternalName", { required: true })}
                      {renderCandidateField(cidx, "Nukh of Maternals", "nukhOfMaternals", { required: true, nukh: true })}
                      {renderCandidateField(cidx, "Place of Maternals", "placeOfMaternals", { required: true })}
                    </div>
                  </div>

                  {/* Education */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2 text-sm">Education & Career</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {renderCandidateField(cidx, "Education", "education", { required: true })}
                      {renderCandidateField(cidx, "Occupation", "occupation", { required: true })}
                      {renderCandidateField(cidx, "Working At", "workingAt", { required: true })}
                      {renderCandidateField(cidx, "Annual Income", "annualIncome")}
                    </div>
                  </div>

                  {/* Contact */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2 text-sm">Contact Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {renderCandidateField(cidx, "Phone", "phone", { required: true })}
                      {renderCandidateField(cidx, "WhatsApp", "whatsapp", { required: true })}
                      {renderCandidateField(cidx, "Email", "email", { required: true })}
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2 text-sm">Address</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      {renderCandidateField(cidx, "Current Address", "currentAddress", { required: true })}
                      {renderCandidateField(cidx, "Place", "currentPlace", { required: true })}
                      {renderCandidateField(cidx, "State", "currentState", { required: true })}
                      {renderCandidateField(cidx, "Country", "currentCountry", { required: true })}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderCandidateField(cidx, "Native Place", "nativePlace", { required: true })}
                      {renderCandidateField(cidx, "Native State", "nativeState", { required: true })}
                    </div>
                  </div>

                  {/* About */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 border-b pb-2 text-sm">About & Preferences</h4>
                    <div className="space-y-4">
                      {renderCandidateTextarea(cidx, "Hobbies & Interests", "hobbies")}
                      {renderCandidateTextarea(cidx, "About Yourself", "aboutCandidate")}
                      {renderCandidateTextarea(cidx, "Partner Preference", "partnerPreference")}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}

        {/* Add Candidate Button */}
        {editMode && (editData.candidates?.length || 0) < 3 && (
          <div className="mb-6">
            <Button variant="outline" onClick={addCandidate} className="w-full border-dashed border-2 border-orange-300 text-orange-600 hover:bg-orange-50 py-6" data-testid="ps-add-candidate-btn">
              <Plus className="w-5 h-5 mr-2" /> Add Another Candidate / एक और उम्मीदवार जोड़ें ({editData.candidates?.length || 0}/3)
            </Button>
          </div>
        )}

        {/* Attending In-Person Toggle */}
        <Card className="p-6 mb-6" data-testid="ps-attending-inperson-section">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900">Attending Event In-Person / कार्यक्रम में व्यक्तिगत रूप से उपस्थित</h3>
              <p className="text-xs text-gray-500 mt-1">Will you be attending the Parichay Sangam 2026 event in person?</p>
            </div>
            {editMode ? (
              <button
                type="button"
                onClick={() => setEditData(prev => ({ ...prev, attendingInPerson: !prev.attendingInPerson }))}
                className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${editData.attendingInPerson ? 'bg-green-500' : 'bg-gray-300'}`}
                data-testid="ps-dash-attending-toggle"
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${editData.attendingInPerson ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            ) : (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${profile.attendingInPerson ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-testid="ps-dash-attending-value">
                {profile.attendingInPerson ? 'Yes / हाँ' : 'No / नहीं'}
              </span>
            )}
          </div>
        </Card>

        {/* Attendees */}
        {(profile.attendingInPerson || profile.attendees?.length > 0 || editMode) && (
          <Card className="p-6 mb-6">
            <h3 className="font-bold text-lg text-gray-900 mb-4 border-b pb-2">Attendees / उपस्थित सदस्य</h3>
            {(editMode ? editData.attendees : profile.attendees)?.length > 0 ? (
              <div className="space-y-3">
                {(editMode ? editData.attendees : profile.attendees).map((a, i) => {
                  const isNewAttendee = i >= (profile.attendees?.length || 0);
                  return (
                    <div key={i} className={`bg-gray-50 p-3 rounded ${isNewAttendee ? 'border-2 border-dashed border-orange-300' : ''}`}>
                      {editMode ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs">Name / नाम <span className="text-destructive">*</span></Label>
                              <Input value={a.name || ''} onChange={(e) => handleAttendeeEdit(i, 'name', e.target.value)} className="mt-1 h-9 text-sm" placeholder="Full name" data-testid={`ps-edit-attendee-name-${i}`} />
                            </div>
                            <div>
                              <Label className="text-xs">Relation / संबंध <span className="text-destructive">*</span></Label>
                              <Input value={a.relation || ''} onChange={(e) => handleAttendeeEdit(i, 'relation', e.target.value)} className="mt-1 h-9 text-sm" placeholder="e.g. Father, Mother" data-testid={`ps-edit-attendee-relation-${i}`} />
                            </div>
                            <div>
                              <Label className="text-xs">Phone / फ़ोन <span className="text-destructive">*</span></Label>
                              <Input value={a.phone || ''} onChange={(e) => handleAttendeeEdit(i, 'phone', e.target.value)} className="mt-1 h-9 text-sm" placeholder="10 digits" data-testid={`ps-edit-attendee-phone-${i}`} />
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 border flex-shrink-0">
                                {a.photoUrl ? <img src={resolveFileUrl(a.photoUrl)} alt={a.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-5 h-5 text-gray-400" /></div>}
                              </div>
                              <button onClick={() => attendeePhotoRefs.current[i]?.click()} disabled={uploadingAttendeePhoto === i}
                                className="text-xs text-orange-600 hover:text-orange-700 font-medium" data-testid={`ps-attendee-photo-change-${i}`}>
                                {uploadingAttendeePhoto === i ? 'Uploading...' : (a.photoUrl ? 'Change Photo' : 'Upload Photo *')}
                              </button>
                              <input ref={el => attendeePhotoRefs.current[i] = el} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleAttendeePhotoUpload(i, e)} />
                              {isNewAttendee && <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">New</span>}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => removeAttendee(i)} className="text-red-500 hover:text-red-700 h-8 w-8 p-0" data-testid={`ps-remove-attendee-${i}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="relative group flex-shrink-0">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200 border">
                              {a.photoUrl ? (
                                <img src={resolveFileUrl(a.photoUrl)} alt={a.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center"><User className="w-6 h-6 text-gray-400" /></div>
                              )}
                            </div>
                            <button onClick={() => attendeePhotoRefs.current[i]?.click()} disabled={uploadingAttendeePhoto === i}
                              className="absolute -bottom-1 -right-1 bg-orange-600 text-white p-1 rounded-full shadow hover:bg-orange-700" data-testid={`ps-attendee-photo-change-${i}`}>
                              {uploadingAttendeePhoto === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                            </button>
                            <input ref={el => attendeePhotoRefs.current[i] = el} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => handleAttendeePhotoUpload(i, e)} />
                          </div>
                          <div className="flex-1 flex justify-between text-sm">
                            <span className="font-medium text-gray-800">{a.name}</span>
                            <span className="text-gray-500">{a.relation} {a.phone && `| ${a.phone}`}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No attendees listed</p>
            )}
            {/* Add Attendee Button */}
            {editMode && (editData.attendees || []).length < 3 && (
              <Button variant="outline" onClick={addAttendee} className="w-full mt-4 border-dashed border-2 border-orange-300 text-orange-600 hover:bg-orange-50" data-testid="ps-add-attendee-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Attendee / सदस्य जोड़ें ({(editData.attendees || []).length}/3)
              </Button>
            )}
          </Card>
        )}

        {/* Additional Info / Feedback + Attachments */}
        <Card className="p-6 mb-6" data-testid="ps-info-attachments-section">
          <h3 className="font-bold text-lg text-gray-900 mb-4 border-b pb-2 flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-gray-600" /> Any Other Information / Feedback
          </h3>

          {/* Additional Info Textarea */}
          {editMode ? (
            <div className="mb-4">
              <Label className="text-sm text-gray-600 mb-1 block">कोई अन्य जानकारी / सुझाव</Label>
              <Textarea
                value={editData.additionalInfo || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, additionalInfo: e.target.value }))}
                placeholder="Enter any additional information or feedback here... / कोई अन्य जानकारी या सुझाव यहाँ लिखें..."
                maxLength={1000}
                className="min-h-[80px] resize-y"
                data-testid="ps-dash-additional-info-input"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{(editData.additionalInfo || '').length}/1000</p>
            </div>
          ) : (
            profile.additionalInfo ? (
              <p className="text-sm text-gray-600 whitespace-pre-wrap mb-4">{profile.additionalInfo}</p>
            ) : (
              <p className="text-sm text-gray-400 italic mb-4">No additional information provided</p>
            )
          )}

          {/* Attachments */}
          <div className="border-t pt-3">
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Attachments / संलग्नक</Label>
            <p className="text-xs text-gray-500 mb-3">Upload additional payment screenshots or supporting documents (max 5 files, JPG/PNG/WEBP/PDF, 5MB each)</p>

          {(profile.infoAttachments?.length > 0) && (
            <div className="space-y-2 mb-3">
              {profile.infoAttachments.map((att, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md text-sm" data-testid={`info-attachment-view-${idx}`}>
                  <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <a href={resolveFileUrl(att.url)} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-blue-600 hover:underline">{att.name}</a>
                  {editMode && (
                    <button type="button" onClick={async () => {
                      try {
                        await axios.delete(`${API}/ps/profile/info-attachment`, { headers: { Authorization: `Bearer ${token}` }, data: { url: att.url } });
                        setProfile(prev => ({ ...prev, infoAttachments: prev.infoAttachments.filter((_, i) => i !== idx) }));
                        toast.success('Attachment removed');
                      } catch { toast.error('Failed to remove attachment'); }
                    }} className="text-red-500 hover:text-red-700" data-testid={`info-attachment-delete-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {editMode && (profile.infoAttachments?.length || 0) < 5 && (
            <label className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50/50 transition-colors text-sm text-gray-600" data-testid="info-attachment-upload-edit-btn">
              {uploadingInfoAttachment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Attachment
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                className="hidden"
                disabled={uploadingInfoAttachment}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Max 5MB'); return; }
                  setUploadingInfoAttachment(true);
                  try {
                    const fd = new FormData();
                    fd.append('file', file);
                    const res = await axios.post(`${API}/ps/profile/info-attachment`, fd, {
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                    });
                    setProfile(prev => ({ ...prev, infoAttachments: [...(prev.infoAttachments || []), res.data.attachment] }));
                    toast.success('Attachment uploaded');
                  } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
                  finally { setUploadingInfoAttachment(false); e.target.value = ''; }
                }}
              />
            </label>
          )}

          {!editMode && (!profile.infoAttachments || profile.infoAttachments.length === 0) && (
            <p className="text-sm text-gray-400 italic">No attachments</p>
          )}
          </div>
        </Card>

        {/* Payment Info (read-only) */}
        {profile.paymentMode && (
          <Card className="p-6 bg-green-50/50">
            <h3 className="font-bold text-lg text-gray-900 mb-4 border-b pb-2">Payment Details / भुगतान विवरण</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><p className="text-xs text-gray-400 mb-0.5">Payment Mode</p><p className="text-sm font-medium text-gray-800">{profile.paymentMode}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Amount</p><p className="text-sm font-medium text-gray-800">{'\u20B9'}{profile.paymentAmount?.toLocaleString()}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Transaction ID / UTR</p><p className="text-sm font-medium text-gray-800">{profile.paymentDetails || '\u2014'}</p></div>
              <div><p className="text-xs text-gray-400 mb-0.5">Candidates + Attendees</p><p className="text-sm font-medium text-gray-800">{profile.candidates?.length || 0} + {profile.attendees?.length || 0}</p></div>
            </div>
          </Card>
        )}

        {editMode && (
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => { setEditMode(false); setEditData(JSON.parse(JSON.stringify(profile))); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="ps-save-bottom-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PSDashboardPage;
